"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, ShieldCheck, Percent, FileSpreadsheet, ArrowRight, Scale } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const faqItems = [{
  id: "item-1",
  question: "How do consulting fees and licensed insurance professional compensation work?",
  answer: `For insurance products, a licensed insurance professional may receive compensation from the insurance company when a policy or annuity contract is issued. The amount and structure of compensation can vary by insurance carrier and product.

DJTD Consulting does not charge a separate fee for its insurance education, product comparisons, or insurance application assistance unless a different arrangement is clearly disclosed in advance.

Product costs, surrender provisions, optional rider charges, and other contract terms should be reviewed separately from how the insurance professional is compensated.`,
  icon: Scale
}, {
  id: "item-2",
  question: "What internal fees exist inside fixed and fixed indexed annuities?",
  answer: `Fees and charges vary by annuity contract. Some fixed and fixed indexed annuities do not have an explicit annual management fee, but that does not mean the contract has no costs or financial limitations.

Depending on the contract, surrender charges, optional rider charges, market value adjustments or other contract adjustments may apply. Fixed indexed annuities can also use caps, participation rates, spreads, or other crediting provisions that affect the amount of interest credited.

Review the specific contract before purchasing an annuity so you understand its charges, limitations, and guarantees.`,
  icon: Percent
}, {
  id: "item-3",
  question: "What are surrender charges and how do they work?",
  answer: `A surrender charge is a contract charge that may apply when more than the permitted amount is withdrawn from an annuity during its surrender-charge period.`,
  icon: HelpCircle
}, {
  id: "item-4",
  question: "How do index caps, participation rates, and spreads affect growth?",
  answer: `These provisions can affect how much index-linked interest is credited to a fixed indexed annuity.

A cap can limit the maximum interest credited under a particular crediting method. A participation rate determines how much of an index gain is used in the interest calculation. A spread generally subtracts a stated amount or percentage when determining credited interest.

These provisions vary by contract and may change as permitted by the contract. Because of these limitations, a fixed indexed annuity generally will not receive the full return of the referenced market index.`,
  icon: FileSpreadsheet
}, {
  id: "item-5",
  question: "How should I compare term life insurance against permanent policies?",
  answer: `Term life insurance provides coverage for a specified period and generally does not build cash value.

Permanent life insurance is designed to provide longer-term or lifetime coverage and may build cash value. Whole life and universal life are examples of permanent insurance.

Premiums, guarantees, cash-value features, flexibility, costs, and other provisions vary by policy. The appropriate type and amount of insurance depend on an individual's needs, goals, circumstances, and budget.`,
  icon: ShieldCheck
}];
export default function Comparisons() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="costs-comparisons" data-nav="light" className="bg-background text-foreground py-20 md:py-28 transition-colors duration-200">
      <div className="mx-auto max-w-[1240px] px-5 sm:px-8 md:px-12">
        {/* Centered Header */}
        <div className="mx-auto max-w-3xl text-center">
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.3
        }} transition={{
          duration: 0.4,
          ease: "easeOut"
        }} className="inline-flex items-center gap-2 mb-4">
            <Badge variant="secondary" className="bg-accent text-foreground font-semibold tracking-wider text-xs px-3 py-1 border border-border">
              TRANSPARENT INSURANCE GUIDANCE
            </Badge>
          </motion.div>

          <motion.h2 initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 14
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.3
        }} transition={{
          duration: 0.45,
          delay: 0.05,
          ease: "easeOut"
        }} className="font-default text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Costs and product comparisons
          </motion.h2>

          <div className="mx-auto mt-4 flex items-center justify-center gap-2">
            <span className="bg-primary size-1.5 rounded-full" />
            <span className="bg-border h-px w-10" />
            <span className="bg-primary size-1.5 rounded-full" />
            <span className="bg-border h-px w-10" />
            <span className="bg-primary size-1.5 rounded-full" />
            <span className="bg-border h-px w-10" />
            <span className="bg-primary size-1.5 rounded-full" />
          </div>

          <motion.p initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 14
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.3
        }} transition={{
          duration: 0.45,
          delay: 0.1,
          ease: "easeOut"
        }} className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            Clear answers about fee structures, compensation models, surrender periods, and how we evaluate different carrier options for your household.
          </motion.p>
        </div>

        {/* Single Column Accordion Container */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.5,
        delay: 0.15,
        ease: "easeOut"
      }} className="mx-auto mt-12 md:mt-16 max-w-[840px]">
          <Accordion type="single" collapsible className="w-full space-y-3">
            {faqItems.map((item, i) => {
            const ItemIcon = item.icon;
            return <div key={item.id} data-index={i}>
                  <AccordionItem value={item.id} className="bg-card text-card-foreground border border-border rounded-lg px-6 py-1 transition-colors duration-200 shadow-2xs hover:border-border/80">
                    <AccordionTrigger className="hover:no-underline py-5 text-left font-semibold text-base sm:text-lg text-foreground [&[data-state=open]]:text-primary gap-4">
                      <div className="flex items-center gap-3.5 pr-2">
                        <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-foreground border border-border">
                          <ItemIcon className="size-4 text-foreground" />
                        </span>
                        <span>{item.question}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pt-1 pb-6 pl-11">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                </div>;
          })}
          </Accordion>

          {/* Bottom Guidance Card */}
          <div className="mt-10 rounded-xl bg-card border border-border p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 shadow-2xs">
            <div className="space-y-1">
              <h3 className="font-semibold text-base sm:text-lg text-foreground">
                Need a specific policy compared?
              </h3>
              <p className="text-sm text-muted-foreground">
                We review existing statements and carrier proposals side by side without sales pressure.
              </p>
            </div>
            <Button asChild size="default" className="bg-primary text-primary-foreground hover:bg-primary/90 shrink-0">
              <Link to="/ContactPage" className="inline-flex items-center gap-2">
                <span>Request comparison</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}